// wikiPublicClient.ts - typed client for the public wiki read surface.
// Unauthenticated: uses apiGetPublic, which never attaches the caller's own
// session token, so a signed-in creator's live token is never sent to
// another creator's public wiki (see docs/editor-consolidation/
// play-origin-isolation.md, the same fix already applied to playClient.ts).
import { apiGetPublic } from '../services/api';

export interface PublicWikiPageSummary {
  id: string;
  slug: string;
  title: string;
  position: number;
  section: string | null;
}

/** Wire shape of `GET /wiki/{slug}`. */
export interface PublicWiki {
  slug: string;
  title: string;
  visibility: 'public' | 'unlisted';
  pages: PublicWikiPageSummary[];
  /** The reverse of the play landing page's wiki link - set only when the
   * project has a *public* game to link back to. */
  game: { slug: string; name: string; url: string } | null;
}

/** Fetch a wiki's metadata and page list. 404s reject (unknown slug and a
 * private wiki are indistinguishable, by design - see the api). */
export function getPublicWiki(slug: string): Promise<PublicWiki> {
  return apiGetPublic<PublicWiki>(`/wiki/${encodeURIComponent(slug)}`);
}

/** Wire shape of `GET /wiki/{slug}/pages/{pageSlug}`. */
export interface PublicWikiPage {
  slug: string;
  title: string;
  body_markdown: string;
  updated_at: string | null;
  wiki: { slug: string; title: string };
}

export function getPublicWikiPage(slug: string, pageSlug: string): Promise<PublicWikiPage> {
  return apiGetPublic<PublicWikiPage>(`/wiki/${encodeURIComponent(slug)}/pages/${encodeURIComponent(pageSlug)}`);
}

/** Wire shape of one `GET /wiki/{slug}/search` result. `snippet` carries no
 * markup (the api disables ts_headline's highlight markers), so it renders
 * as plain text same as any other string here. */
export interface WikiSearchResult {
  id: string;
  slug: string;
  title: string;
  snippet: string;
}

/** Full-text search across a single wiki's own pages. An empty/blank query
 * is never sent - the api treats it as "no results" rather than "everything",
 * and there is no reason to round-trip for a query the caller already knows
 * is empty. */
export function searchWiki(slug: string, query: string): Promise<WikiSearchResult[]> {
  const q = query.trim();
  if (!q) return Promise.resolve([]);
  return apiGetPublic<WikiSearchResult[]>(`/wiki/${encodeURIComponent(slug)}/search?q=${encodeURIComponent(q)}`);
}

/** Wire shape of one `GET /wiki` directory entry. */
export interface PublicWikiListing {
  slug: string;
  title: string;
  updated_at: string | null;
  page_count: number;
  game: { slug: string; name: string; url: string } | null;
  dev: { slug: string; studio_name: string } | null;
}

/** Every publicly visible wiki, most recently updated first - the same
 * discovery-vs-read split as listPublicGames (playClient.ts): unlisted and
 * private wikis never appear here, by definition. */
export function listPublicWikis(): Promise<PublicWikiListing[]> {
  return apiGetPublic<PublicWikiListing[]>('/wiki');
}
